/**
 * The Starter App Template as a PROJECT — the folder the student downloads.
 *
 * Until now the reward for finishing the build spec (V2401 / V3401) was a single
 * `index.html`: a landing page with the spec in an HTML comment at the top. That is
 * everything a beginner needs on day one and nothing they need on day thirty. This
 * builds the folder instead — eleven small files that already know how to sign a
 * person in, save their data, and be deployed.
 *
 * THE ONE DESIGN IDEA: THE SEAM
 *
 * The project runs with NO Firebase setup at all. `config.js` ships with
 * `firebase: null`, and `store.js` quietly uses `localStorage`. When the student is
 * ready — because they want their data on a second device, real accounts, or other
 * people using the app — they paste one object into `config.js` and nothing else
 * changes. Same six functions, same markup, same CSS.
 *
 * That matters pedagogically as much as practically. `store.js` holds the two
 * implementations side by side, so "what is a database, really?" is answered by
 * reading one file where the same six function names are written twice.
 *
 * WHY THESE FILES AND NOT A REAL FRAMEWORK
 *
 * Three constraints, all of them non-negotiable for this audience:
 *
 *   1. NO BUILD STEP. The student double-clicks `index.html` and their app opens.
 *      Anything that needs `npm install` first destroys the day-one win the whole
 *      course is built around.
 *   2. NO ES MODULES. A page opened over `file://` cannot `import` a sibling file —
 *      the browser blocks it as a cross-origin request from an opaque origin. So
 *      every script here is a classic `<script src>` that hangs its API off
 *      `window`, and Firebase arrives through its `-compat` builds, which are
 *      plain scripts rather than modules.
 *   3. NO `.json` CONFIG. Same reason: reading `brand.json` would need `fetch()`,
 *      which `file://` also blocks. The config has to be a `.js` file that assigns
 *      a global, which is why `config.js` looks the way it does.
 *
 * The one place the constraints bite is Firebase Auth, which does not work from a
 * `file://` origin. That is fine, and deliberate: it only bites AFTER the student
 * chooses to connect Firebase, by which point they are in Module 5/6 with Claude
 * Code able to start a local server for them. CONNECT-FIREBASE.md opens by saying so
 * rather than letting them discover it.
 *
 * LOCALIZATION
 *
 * Everything the student reads is bilingual — the three markdown files, the visible
 * copy in `app.html`, and the CODE COMMENTS, which for a Spanish-first course are
 * not decoration. Following authoredLessons.js: identifiers, CSS classes, element
 * ids and storage keys are IDENTICAL across locales, and only words change. That is
 * what lets projectTemplate.test.js assert both languages produce the same file set
 * with the same structure.
 */

import { buildStarterAppHtml, starterAppCss, esc, DEFAULT_BRAND } from "./starterAppHtml.js";

/** Pinned rather than floating: a student's project should not change behaviour
 *  because Google shipped a new minor while they were asleep. Matches the version
 *  CodeNec itself depends on. */
const FIREBASE_VERSION = "10.12.2";

const FIREBASE_SDKS = ["app", "auth", "firestore"].map(
    (part) => `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/firebase-${part}-compat.js`
);

/** Embed a value in generated JavaScript. `JSON.stringify` is the correct escaper
 *  for a JS string literal — quotes, backslashes, newlines and all. */
const js = (value) => JSON.stringify(value == null ? "" : String(value));

/* ------------------------------------------------------------------------- *
 * Copy
 * ------------------------------------------------------------------------- */

const COPY = {
    es: {
        // --- app.html ---
        appTitle: "Mi app",
        backToHome: "Inicio",
        signOut: "Cerrar sesión",
        modeLocal:
            "Guardando en este navegador. Tus datos viven en esta computadora y nadie más los ve. Cuando quieras que estén en la nube, abre CONNECT-FIREBASE.md.",
        modeCloud: "Conectada a Firebase. Tus datos se guardan en la nube y te siguen a cualquier dispositivo.",
        signInTitle: "Entra a tu app",
        signInHint:
            "Ahora mismo esto no revisa ninguna contraseña: solo guarda tu nombre en este navegador para que la app tenga la misma forma que tendrá cuando la conectes a Firebase.",
        signInHintCloud: "Usa tu correo y una contraseña de al menos 6 caracteres.",
        emailLabel: "Tu correo",
        passwordLabel: "Contraseña",
        signInBtn: "Entrar",
        signUpBtn: "Crear cuenta",
        itemsTitle: "Tu lista",
        itemsHint: "Agrega algo y sigue ahí cuando vuelvas mañana.",
        addPlaceholder: "Escribe algo…",
        addBtn: "Agregar",
        emptyList: "Todavía no hay nada. Agrega lo primero.",
        removeBtn: "Borrar",
        aiTitle: "Pregúntale a la IA",
        aiHint: "Esta parte todavía no está conectada. Abre CLAUDE.md para ver cómo conectarla.",
        aiPlaceholder: "Hazle una pregunta a tu app…",
        aiBtn: "Preguntar",
        aiStub:
            "Todavía no estoy conectada a una IA de verdad. Para conectarme hace falta un servidor pequeño que guarde la llave secreta: una llave de API dentro de esta carpeta sería pública para cualquiera que abra tu app. Búscalo en CLAUDE.md, en la sección «La función de IA».",
        errSignIn: "No pudimos entrar. Revisa el correo y la contraseña.",
        errSave: "No pudimos guardar. Revisa tu conexión.",
        errStart:
            "No pudimos conectar con Firebase. Revisa tu internet y que la configuración de config.js esté completa.",
        loading: "Cargando…",

        // --- code comments ---
        cfgHeader: "El ÚNICO archivo que editas para conectar tu app a Firebase.",
        cfgBrand: "El nombre, el ícono y la frase de tu app. Cámbialos cuando quieras.",
        cfgColorNote: "¿El color? Está en styles.css, en la primera línea (--accent).",
        cfgFirebaseOff: [
            "Déjalo en null y tu app guarda todo en ESTE navegador.",
            "Funciona sin internet, sin cuenta y sin configurar nada.",
            "",
            "Cuando quieras cuentas de verdad y datos en la nube, pega aquí el",
            "objeto de configuración que te da Firebase. Paso a paso, con las",
            "capturas de cada botón: CONNECT-FIREBASE.md",
        ],
        // Los nombres `localStore` y `cloudStore` son los de verdad, en inglés como
        // todo identificador del proyecto (ver la nota de LOCALIZATION arriba).
        storeHeader: [
            "Entrar, salir y guardar datos. Las mismas seis funciones, siempre.",
            "",
            "Abajo hay DOS versiones de lo mismo: `localStore` escribe en este",
            "navegador y `cloudStore` escribe en Firestore. La app de arriba",
            "(app.js) no sabe cuál está usando, y por eso conectar Firebase no la",
            "obliga a cambiar ni una línea.",
        ],
        storePick: "Si config.js tiene una configuración de Firebase, usamos la nube. Si no, este navegador.",
        storeLocalNote: "Cada app tiene su propia caja: dos apps abiertas desde archivos no se pisan los datos.",
        storeCloudNote: "users/{uid}/items — cada persona escribe solo bajo su propio uid. Eso es lo que dicen las reglas en firestore.rules.",
        appJsHeader: [
            "El código de TU app. Este es el archivo donde tú y Claude van a trabajar.",
            "",
            "No habla con Firebase ni con localStorage: solo le pide cosas a `store`.",
        ],

        // --- README.md ---
        readmeIntro: "Esta es tu app. La creaste en CodeNec y ahora vive en tu computadora.",
        readmeRunTitle: "Cómo abrirla",
        readmeRunBody: [
            "Haz doble clic en `index.html`. Se abre en tu navegador. Ya está.",
            "",
            "No hay que instalar nada. No hay que compilar nada. Lo que agregues a tu lista",
            "sigue ahí cuando cierres el navegador y vuelvas mañana.",
        ],
        readmeFilesTitle: "Qué es cada archivo",
        readmeNextTitle: "Qué sigue",
        // El número de módulo salió de aquí a propósito: este mismo proyecto se descarga
        // en tres cursos que numeran sus módulos distinto, así que cualquier número está
        // mal en dos de ellos.
        readmeNextBody: [
            "1. Ábrela con Claude Code y pídele un cambio.",
            "2. Cuando quieras cuentas de verdad y datos en la nube: `CONNECT-FIREBASE.md`.",
            "3. Cuando quieras que otros la usen, con tu propio Project ID:",
            "   `firebase deploy --only hosting --project TU-PROJECT-ID`",
        ],

        // --- CLAUDE.md ---
        claudeIntro: [
            "Este archivo es para Claude. Descríbele el proyecto una vez y no tendrás que",
            "explicárselo en cada sesión.",
        ],
        claudeWhatTitle: "Qué es este proyecto",
        claudeStackTitle: "Cómo está hecho",
        claudeStackBody: [
            "HTML, CSS y JavaScript sin frameworks y sin paso de compilación. Se abre",
            "haciendo doble clic en `index.html`.",
            "",
            "REGLAS QUE NO SE ROMPEN:",
            "",
            "- Nada de módulos ES (`import` / `export`) ni de `fetch()` a archivos locales.",
            "  Esta app se abre con `file://` y el navegador bloquea las dos cosas. Usa",
            "  `<script src=\"...\"></script>` normales y variables en `window`.",
            "- Nada de `npm install` ni de empaquetadores. Si hace falta una librería, va",
            "  por CDN con una etiqueta `<script>`.",
            "- Nada de llaves de API en estos archivos. Cualquiera que abra la app las ve.",
            "- Todo lo que lea o escriba datos pasa por `store` (store.js). `app.js` nunca",
            "  llama a Firebase ni a localStorage directamente.",
        ],
        claudeFilesTitle: "Los archivos",
        claudeDataTitle: "Dónde se guardan los datos",
        claudeDataBody: [
            "`store.js` tiene dos implementaciones de las mismas seis funciones y elige una",
            "sola vez al cargar:",
            "",
            "- Sin configuración de Firebase en `config.js` → `localStorage`, en este navegador.",
            "- Con configuración → Firestore, en `users/{uid}/items`.",
            "",
            "Para agregar un campo o una colección, cámbialo en LAS DOS. Si solo cambias una,",
            "la app se comporta distinto según esté conectada o no, y ese error es muy difícil",
            "de encontrar.",
        ],
        claudeAiTitle: "La función de IA",
        claudeAiBody: [
            "`store.askAI()` devuelve una respuesta de mentira a propósito.",
            "",
            "Para conectarla de verdad hace falta un servidor pequeño que guarde la llave de",
            "la API, porque una llave puesta en estos archivos sería pública. Las dos formas",
            "normales son una Cloud Function de Firebase (necesita el plan Blaze, de pago) o",
            "un servidor mínimo en Node. Cuando exista, `store.askAI()` es el único lugar que",
            "hay que cambiar.",
        ],
        claudeSpecTitle: "Qué se supone que debe llegar a ser",
        claudeSpecMissing: [
            "Todavía no hay una especificación escrita. Pregúntale a la persona que",
            "construye esto qué quiere que haga la app y escríbela aquí.",
        ],

        // --- CONNECT-FIREBASE.md ---
        fbIntro: [
            "Tu app YA FUNCIONA sin esto. Guarda tus datos en tu navegador y no necesita",
            "cuenta ni internet.",
            "",
            "Conéctala a Firebase cuando quieras una de estas tres cosas:",
            "",
            "- Que tus datos estén en la nube y te sigan a tu teléfono.",
            "- Cuentas de verdad, con correo y contraseña.",
            "- Que otras personas usen tu app.",
        ],
        fbWarnTitle: "Antes de empezar: una cosa cambia",
        fbWarnBody: [
            "Hasta ahora abrías tu app con doble clic. Al conectar Firebase, eso deja de",
            "alcanzar: el sistema de cuentas de Firebase no funciona con direcciones que",
            "empiezan con `file://`.",
            "",
            "A partir de ese momento tienes que abrir tu app de una de estas dos formas:",
            "",
            "- Pídele a Claude Code: **levanta un servidor local para esta carpeta**.",
            "- O publícala en internet y ábrela desde su dirección. Eso necesita Node y la",
            "  herramienta de Firebase instalada; el curso te lleva paso a paso. El comando",
            "  es `firebase deploy --only hosting --project TU-PROJECT-ID`.",
            "",
            "No es un error tuyo ni algo que se pueda arreglar. Es así para todas las apps",
            "que usan cuentas.",
        ],
        fbStepsTitle: "Los pasos",
        fbSteps: [
            {
                title: "Crea el proyecto — o reutiliza el que ya tienes",
                body: [
                    "**Si el curso ya te hizo crear un proyecto de Firebase para publicar tu app,",
                    "usa ese mismo y sáltate este paso.** No crees un segundo: tu app quedaría",
                    "publicada en un proyecto y sus datos en otro, no saldría ningún error, y el",
                    "sitio en vivo se vería vacío.",
                    "",
                    "¿No tienes uno? Entra a <https://console.firebase.google.com> con tu cuenta",
                    "de Google y haz clic en **Agregar proyecto**. Ponle el nombre de tu app.",
                    "Puedes desactivar Google Analytics: no lo necesitas.",
                    "",
                    "Sea cual sea el camino, abre **⚙️ Project settings** y anota el **Project",
                    "ID**. No siempre es el nombre que escribiste, y es lo que necesita el",
                    "comando para publicar.",
                ],
            },
            {
                title: "Registra la app web",
                body: [
                    "En la pantalla principal del proyecto busca el ícono `</>` (**Web**). Ponle",
                    "un apodo y haz clic en **Registrar app**.",
                    "",
                    "Te va a mostrar un bloque de código con `const firebaseConfig = { ... }`.",
                    "Ese objeto entre llaves es lo que necesitas. Cópialo.",
                ],
            },
            {
                title: "Pégalo en config.js",
                body: [
                    "Abre `config.js` y cambia `firebase: null` por el objeto que copiaste:",
                    "",
                    "```js",
                    "firebase: {",
                    "  apiKey: \"...\",",
                    "  authDomain: \"tu-proyecto.firebaseapp.com\",",
                    "  projectId: \"tu-proyecto\",",
                    "  storageBucket: \"tu-proyecto.appspot.com\",",
                    "  messagingSenderId: \"...\",",
                    "  appId: \"...\"",
                    "},",
                    "```",
                    "",
                    "Esa `apiKey` NO es una contraseña y no pasa nada si alguien la ve: Firebase",
                    "la diseñó para estar a la vista. Lo que de verdad protege tus datos es el",
                    "paso 6.",
                ],
            },
            {
                title: "Enciende las cuentas",
                body: [
                    "En el menú de la izquierda: **Compilación → Authentication → Comenzar**.",
                    "Elige **Correo electrónico/contraseña** y actívalo con **Habilitar**.",
                    "Guarda.",
                ],
            },
            {
                title: "Crea la base de datos",
                body: [
                    "**Compilación → Firestore Database → Crear base de datos**. Elige la región",
                    "más cercana a donde vive tu gente. Si te pregunta por el modo, elige",
                    "**modo de producción**: el siguiente paso pone las reglas correctas.",
                ],
            },
            {
                title: "Pon las reglas de seguridad",
                body: [
                    "Este es el paso importante, y es el que se salta casi todo el mundo.",
                    "",
                    "En Firestore, abre la pestaña **Reglas**. Borra lo que haya y pega el",
                    "contenido de `firestore.rules` (está en esta misma carpeta). Publica.",
                    "",
                    "Sin esto, cualquier persona en internet puede leer y borrar los datos de",
                    "todos tus usuarios. Las reglas que te dejamos dicen una sola cosa: cada",
                    "quien puede ver y cambiar lo suyo, y nada más.",
                ],
            },
            {
                title: "Ábrela con un servidor",
                body: [
                    "Pídele a Claude Code: **levanta un servidor local para esta carpeta y dime**",
                    "**en qué dirección la abro**. Abre esa dirección en tu navegador.",
                    "",
                    "Arriba de tu app debe decir que está conectada a Firebase. Crea una cuenta,",
                    "agrega algo a tu lista, y búscalo en la consola de Firebase: ahí está.",
                ],
            },
        ],
        fbTroubleTitle: "Si algo sale mal",
        fbTrouble: [
            "**Sigue diciendo que guarda en este navegador.** `config.js` no quedó bien",
            "guardado, o falta la `apiKey` dentro del objeto.",
            "",
            "**`auth/operation-not-allowed`.** Falta el paso 4: el método de correo y",
            "contraseña no está activado.",
            "",
            "**`Missing or insufficient permissions`.** Falta el paso 6: las reglas.",
            "",
            "**`auth/unauthorized-domain` o nada pasa al entrar.** Estás abriendo la app con",
            "doble clic. Vuelve a leer la sección de arriba.",
            "",
            "Copia el mensaje de error completo y pégaselo a Claude. Para eso está.",
        ],

        // --- firestore.rules ---
        rulesComment: [
            "Quién puede leer y escribir tus datos.",
            "",
            "Una sola regla: para entrar tienes que haber iniciado sesión, y solo puedes",
            "tocar lo que está bajo TU propio uid. Nadie puede leer la lista de otra",
            "persona aunque adivine su uid.",
            "",
            "Cópialas en la consola de Firebase: Firestore Database → Reglas → Publicar.",
        ],
    },

    en: {
        // --- app.html ---
        appTitle: "My app",
        backToHome: "Home",
        signOut: "Sign out",
        modeLocal:
            "Saving in this browser. Your data lives on this computer and nobody else can see it. When you want it in the cloud, open CONNECT-FIREBASE.md.",
        modeCloud: "Connected to Firebase. Your data is saved in the cloud and follows you to any device.",
        signInTitle: "Sign in to your app",
        signInHint:
            "Right now this checks no password at all — it just remembers your name in this browser, so the app has the same shape it will have once you connect Firebase.",
        signInHintCloud: "Use your email and a password of at least 6 characters.",
        emailLabel: "Your email",
        passwordLabel: "Password",
        signInBtn: "Sign in",
        signUpBtn: "Create account",
        itemsTitle: "Your list",
        itemsHint: "Add something and it will still be there tomorrow.",
        addPlaceholder: "Type something…",
        addBtn: "Add",
        emptyList: "Nothing here yet. Add the first thing.",
        removeBtn: "Delete",
        aiTitle: "Ask the AI",
        aiHint: "This part isn't connected yet. Open CLAUDE.md to see how to connect it.",
        aiPlaceholder: "Ask your app a question…",
        aiBtn: "Ask",
        aiStub:
            "I'm not connected to a real AI yet. Connecting me needs a small server to hold the secret key: an API key inside this folder would be public to anyone who opens your app. Look for \"The AI feature\" in CLAUDE.md.",
        errSignIn: "We couldn't sign you in. Check the email and password.",
        errSave: "We couldn't save that. Check your connection.",
        errStart:
            "We couldn't reach Firebase. Check your internet, and that the config in config.js is complete.",
        loading: "Loading…",

        // --- code comments ---
        cfgHeader: "The ONLY file you edit to connect your app to Firebase.",
        cfgBrand: "Your app's name, icon and tagline. Change them whenever you like.",
        cfgColorNote: "The colour? That's in styles.css, first line (--accent).",
        cfgFirebaseOff: [
            "Leave this as null and your app saves everything in THIS browser.",
            "It works offline, with no account and nothing to set up.",
            "",
            "When you want real accounts and data in the cloud, paste the config",
            "object Firebase gives you right here. Step by step, with the name of",
            "every button: CONNECT-FIREBASE.md",
        ],
        storeHeader: [
            "Signing in, signing out, and saving data. The same six functions, always.",
            "",
            "Below are TWO versions of the same thing: `localStore` writes to this",
            "browser and `cloudStore` writes to Firestore. The app above (app.js) has",
            "no idea which one it's using — which is why connecting Firebase doesn't",
            "make it change a single line.",
        ],
        storePick: "If config.js has a Firebase config, use the cloud. If not, this browser.",
        storeLocalNote: "Each app gets its own box, so two apps opened from files don't overwrite each other.",
        storeCloudNote: "users/{uid}/items — everyone writes only under their own uid. That's exactly what firestore.rules says.",
        appJsHeader: [
            "YOUR app's code. This is the file you and Claude are going to work in.",
            "",
            "It never talks to Firebase or localStorage — it only asks `store` for things.",
        ],

        // --- README.md ---
        readmeIntro: "This is your app. You built it on CodeNec and now it lives on your computer.",
        readmeRunTitle: "How to open it",
        readmeRunBody: [
            "Double-click `index.html`. It opens in your browser. That's it.",
            "",
            "Nothing to install. Nothing to compile. Whatever you add to your list is still",
            "there when you close the browser and come back tomorrow.",
        ],
        readmeFilesTitle: "What each file is",
        readmeNextTitle: "What's next",
        // The module number came out of here on purpose: this same project downloads in
        // three courses that number their modules differently, so any number is wrong in
        // two of them.
        readmeNextBody: [
            "1. Open it with Claude Code and ask for a change.",
            "2. When you want real accounts and cloud data: `CONNECT-FIREBASE.md`.",
            "3. When you want other people to use it, with your own Project ID:",
            "   `firebase deploy --only hosting --project YOUR-PROJECT-ID`",
        ],

        // --- CLAUDE.md ---
        claudeIntro: [
            "This file is for Claude. Describe the project once here and you won't have to",
            "explain it again every session.",
        ],
        claudeWhatTitle: "What this project is",
        claudeStackTitle: "How it's built",
        claudeStackBody: [
            "Plain HTML, CSS and JavaScript. No framework, no build step. It opens by",
            "double-clicking `index.html`.",
            "",
            "RULES THAT DO NOT BEND:",
            "",
            "- No ES modules (`import` / `export`) and no `fetch()` of local files. This app",
            "  is opened over `file://` and the browser blocks both. Use ordinary",
            "  `<script src=\"...\"></script>` tags and globals on `window`.",
            "- No `npm install`, no bundler. If a library is needed, load it from a CDN with",
            "  a `<script>` tag.",
            "- No API keys in these files. Anyone who opens the app can read them.",
            "- Everything that reads or writes data goes through `store` (store.js). `app.js`",
            "  never calls Firebase or localStorage directly.",
        ],
        claudeFilesTitle: "The files",
        claudeDataTitle: "Where the data lives",
        claudeDataBody: [
            "`store.js` holds two implementations of the same six functions and picks one,",
            "once, at load time:",
            "",
            "- No Firebase config in `config.js` → `localStorage`, in this browser.",
            "- Config present → Firestore, at `users/{uid}/items`.",
            "",
            "To add a field or a collection, change BOTH. Changing only one makes the app",
            "behave differently depending on whether it's connected, and that bug is a",
            "miserable one to find.",
        ],
        claudeAiTitle: "The AI feature",
        claudeAiBody: [
            "`store.askAI()` returns a fake answer on purpose.",
            "",
            "Connecting it for real needs a small server to hold the API key, because a key",
            "placed in these files would be public. The two normal ways are a Firebase Cloud",
            "Function (needs the paid Blaze plan) or a tiny Node server. When one exists,",
            "`store.askAI()` is the only place that has to change.",
        ],
        claudeSpecTitle: "What it's supposed to become",
        claudeSpecMissing: [
            "There's no written spec yet. Ask the person building this what they want the",
            "app to do, and write it down here.",
        ],

        // --- CONNECT-FIREBASE.md ---
        fbIntro: [
            "Your app ALREADY WORKS without this. It saves your data in your browser and",
            "needs no account and no internet.",
            "",
            "Connect Firebase when you want one of these three things:",
            "",
            "- Your data in the cloud, following you to your phone.",
            "- Real accounts, with an email and a password.",
            "- Other people using your app.",
        ],
        fbWarnTitle: "Before you start: one thing changes",
        fbWarnBody: [
            "Until now you opened your app by double-clicking it. Once Firebase is connected",
            "that stops being enough: Firebase's account system does not work with addresses",
            "that start with `file://`.",
            "",
            "From then on you have to open your app one of these two ways:",
            "",
            "- Ask Claude Code: **start a local server for this folder**.",
            "- Or publish it to the internet and open it from its web address. That needs",
            "  Node and the Firebase tool installed; the course walks you through it. The",
            "  command is `firebase deploy --only hosting --project YOUR-PROJECT-ID`.",
            "",
            "This isn't a mistake you made and it isn't fixable. It's true of every app that",
            "has accounts.",
        ],
        fbStepsTitle: "The steps",
        fbSteps: [
            {
                title: "Create the project — or reuse the one you have",
                body: [
                    "**If the course already had you make a Firebase project to publish your app,",
                    "use that same one and skip this step.** Do not make a second: your app would",
                    "be published under one project while its data lives in another, nothing would",
                    "show an error, and the live site would just come up empty.",
                    "",
                    "Don't have one? Go to <https://console.firebase.google.com> with your Google",
                    "account and click **Add project**. Name it after your app. You can turn",
                    "Google Analytics off — you don't need it.",
                    "",
                    "Either way, open **⚙️ Project settings** and write down the **Project ID**.",
                    "It is not always the name you typed, and it is what the publish command",
                    "needs.",
                ],
            },
            {
                title: "Register the web app",
                body: [
                    "On the project's main screen find the `</>` icon (**Web**). Give it a",
                    "nickname and click **Register app**.",
                    "",
                    "It shows you a block of code with `const firebaseConfig = { ... }`. That",
                    "object in the braces is what you need. Copy it.",
                ],
            },
            {
                title: "Paste it into config.js",
                body: [
                    "Open `config.js` and replace `firebase: null` with the object you copied:",
                    "",
                    "```js",
                    "firebase: {",
                    "  apiKey: \"...\",",
                    "  authDomain: \"your-project.firebaseapp.com\",",
                    "  projectId: \"your-project\",",
                    "  storageBucket: \"your-project.appspot.com\",",
                    "  messagingSenderId: \"...\",",
                    "  appId: \"...\"",
                    "},",
                    "```",
                    "",
                    "That `apiKey` is NOT a password and it's fine for people to see it —",
                    "Firebase designed it to be public. The thing that actually protects your",
                    "data is step 6.",
                ],
            },
            {
                title: "Turn on accounts",
                body: [
                    "In the left menu: **Build → Authentication → Get started**. Choose",
                    "**Email/Password** and switch it on with **Enable**. Save.",
                ],
            },
            {
                title: "Create the database",
                body: [
                    "**Build → Firestore Database → Create database**. Pick the region closest",
                    "to where your people live. If it asks about a mode, choose **production**",
                    "**mode** — the next step puts the right rules in place.",
                ],
            },
            {
                title: "Put the security rules in",
                body: [
                    "This is the step that matters, and it's the one almost everybody skips.",
                    "",
                    "In Firestore, open the **Rules** tab. Delete what's there and paste the",
                    "contents of `firestore.rules` (it's in this folder). Publish.",
                    "",
                    "Without this, anyone on the internet can read and delete every one of your",
                    "users' data. The rules we've given you say one thing: each person can see",
                    "and change their own things, and nothing else.",
                ],
            },
            {
                title: "Open it through a server",
                body: [
                    "Ask Claude Code: **start a local server for this folder and tell me what**",
                    "**address to open**. Open that address in your browser.",
                    "",
                    "The top of your app should now say it's connected to Firebase. Create an",
                    "account, add something to your list, then go look for it in the Firebase",
                    "console — there it is.",
                ],
            },
        ],
        fbTroubleTitle: "If something goes wrong",
        fbTrouble: [
            "**It still says it's saving in this browser.** `config.js` didn't save properly,",
            "or the `apiKey` is missing from inside the object.",
            "",
            "**`auth/operation-not-allowed`.** Step 4 is missing — email and password sign-in",
            "isn't switched on.",
            "",
            "**`Missing or insufficient permissions`.** Step 6 is missing — the rules.",
            "",
            "**`auth/unauthorized-domain`, or nothing happens when you sign in.** You're",
            "opening the app by double-clicking it. Read the section above again.",
            "",
            "Copy the whole error message and paste it to Claude. That's what it's for.",
        ],

        // --- firestore.rules ---
        rulesComment: [
            "Who is allowed to read and write your data.",
            "",
            "One rule: you have to be signed in, and you can only touch what's under YOUR",
            "own uid. Nobody can read another person's list even if they guess their uid.",
            "",
            "Copy this into the Firebase console: Firestore Database → Rules → Publish.",
        ],
    },
};

/**
 * One-line description of every file, shown in README.md and CLAUDE.md.
 *
 * Kept beside the file map it describes so a new file cannot be added without a
 * line here — projectTemplate.test.js asserts the two lists match exactly.
 */
const FILE_NOTES = {
    es: {
        "index.html": "La página que ve la gente cuando llega. Es la que armaste en el Módulo 1.",
        "app.html": "Tu app: entrar, la lista y la parte de IA.",
        "styles.css": "Todo el diseño. El color de tu app es la primera línea.",
        "config.js": "El nombre de tu app y la conexión a Firebase. El único archivo que tienes que editar para conectarla.",
        "store.js": "Entrar y guardar datos. Las mismas seis funciones, escritas dos veces: una para este navegador y otra para la nube.",
        "app.js": "El código de tu app. Aquí es donde trabajas tú.",
        "CLAUDE.md": "Lo que Claude lee para entender tu proyecto sin que se lo expliques.",
        "README.md": "Cómo abrir tu app y qué es cada archivo.",
        "CONNECT-FIREBASE.md": "Cómo conectar tu app a Firebase, paso a paso, cuando lo necesites.",
        "firebase.json": "Para publicarla en internet con `firebase deploy --only hosting`.",
        "firestore.rules": "Quién puede ver y cambiar tus datos. Va en la consola de Firebase.",
    },
    en: {
        "index.html": "The page people land on. It's the one you made in Module 1.",
        "app.html": "Your app: signing in, the list, and the AI part.",
        "styles.css": "All the styling. Your app's colour is the first line.",
        "config.js": "Your app's name and its Firebase connection. The only file you have to edit to connect it.",
        "store.js": "Signing in and saving data. The same six functions written twice: once for this browser, once for the cloud.",
        "app.js": "Your app's code. This is where you work.",
        "CLAUDE.md": "What Claude reads to understand your project without you explaining it.",
        "README.md": "How to open your app, and what each file is.",
        "CONNECT-FIREBASE.md": "How to connect your app to Firebase, step by step, when you need it.",
        "firebase.json": "For publishing it to the internet with `firebase deploy --only hosting`.",
        "firestore.rules": "Who can see and change your data. Goes in the Firebase console.",
    },
};

/* ------------------------------------------------------------------------- *
 * Small builders
 * ------------------------------------------------------------------------- */

const lines = (value) => (Array.isArray(value) ? value.join("\n") : String(value || ""));

/** Prefix each line of a block with `//` so multi-line copy can be a code comment. */
const commentBlock = (value, prefix = "// ") =>
    lines(value)
        .split("\n")
        .map((line) => (line ? prefix + line : prefix.trimEnd()))
        .join("\n");

/* ------------------------------------------------------------------------- *
 * The generated files
 * ------------------------------------------------------------------------- */

const buildConfigJs = (brand, c) => `${commentBlock(c.cfgHeader)}

window.APP_CONFIG = {

  ${commentBlock(c.cfgBrand, "// ").trim()}
  ${commentBlock(c.cfgColorNote, "// ").trim()}
  brand: {
    appName: ${js(brand.appName)},
    emoji: ${js(brand.emoji)},
    tagline: ${js(brand.tagline)},
  },

${commentBlock(c.cfgFirebaseOff, "  // ")}
  firebase: null,

};
`;

const buildStoreJs = (c) => `${commentBlock(c.storeHeader)}

(function () {
  "use strict";

  var config = (window.APP_CONFIG || {});
  var firebaseConfig = config.firebase || null;

  // ${lines(c.storePick)}
  var useCloud = !!(firebaseConfig && firebaseConfig.apiKey);

  var FIREBASE_SDKS = [
${FIREBASE_SDKS.map((url) => `    ${js(url)}`).join(",\n")}
  ];

  // ------------------------------------------------------------------
  // ${lines(c.storeLocalNote)}
  // ------------------------------------------------------------------

  var KEY_PREFIX = "app:" + (config.brand && config.brand.appName ? config.brand.appName : "my-app") + ":";

  function read(name, fallback) {
    try {
      var raw = window.localStorage.getItem(KEY_PREFIX + name);
      return raw ? JSON.parse(raw) : fallback;
    } catch (err) {
      return fallback;
    }
  }

  function write(name, value) {
    try {
      window.localStorage.setItem(KEY_PREFIX + name, JSON.stringify(value));
      return true;
    } catch (err) {
      return false;
    }
  }

  var localStore = {
    start: function () {
      return Promise.resolve();
    },
    currentUser: function () {
      return read("user", null);
    },
    signIn: function (email) {
      var user = { uid: "local", email: email };
      write("user", user);
      return Promise.resolve(user);
    },
    signUp: function (email) {
      return localStore.signIn(email);
    },
    signOut: function () {
      write("user", null);
      return Promise.resolve();
    },
    list: function () {
      return Promise.resolve(read("items", []));
    },
    add: function (text) {
      var items = read("items", []);
      var item = { id: String(Date.now()), text: text };
      items.push(item);
      write("items", items);
      return Promise.resolve(item);
    },
    remove: function (id) {
      write("items", read("items", []).filter(function (item) { return item.id !== id; }));
      return Promise.resolve();
    },
  };

  // ------------------------------------------------------------------
  // ${lines(c.storeCloudNote)}
  // ------------------------------------------------------------------

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var el = document.createElement("script");
      el.src = src;
      el.onload = resolve;
      el.onerror = function () { reject(new Error("Could not load " + src)); };
      document.head.appendChild(el);
    });
  }

  var cloudStore = {
    start: function () {
      return FIREBASE_SDKS.reduce(function (chain, src) {
        return chain.then(function () { return loadScript(src); });
      }, Promise.resolve()).then(function () {
        window.firebase.initializeApp(firebaseConfig);

        // Firebase restores a signed-in session ASYNCHRONOUSLY, so right after
        // initializeApp() currentUser is still null even for someone who signed
        // in yesterday. Waiting for the first auth event here is what stops a
        // returning user from being shown the sign-in form for no reason.
        return new Promise(function (resolve) {
          var stop = window.firebase.auth().onAuthStateChanged(function () {
            stop();
            resolve();
          });
        });
      });
    },
    currentUser: function () {
      var user = window.firebase.auth().currentUser;
      return user ? { uid: user.uid, email: user.email } : null;
    },
    signIn: function (email, password) {
      return window.firebase.auth().signInWithEmailAndPassword(email, password)
        .then(function (result) { return { uid: result.user.uid, email: result.user.email }; });
    },
    signUp: function (email, password) {
      return window.firebase.auth().createUserWithEmailAndPassword(email, password)
        .then(function (result) { return { uid: result.user.uid, email: result.user.email }; });
    },
    signOut: function () {
      return window.firebase.auth().signOut();
    },
    items: function () {
      var uid = window.firebase.auth().currentUser.uid;
      return window.firebase.firestore().collection("users").doc(uid).collection("items");
    },
    list: function () {
      return cloudStore.items().orderBy("createdAt").get().then(function (snapshot) {
        return snapshot.docs.map(function (doc) {
          return { id: doc.id, text: doc.data().text };
        });
      });
    },
    add: function (text) {
      return cloudStore.items().add({
        text: text,
        createdAt: window.firebase.firestore.FieldValue.serverTimestamp(),
      }).then(function (ref) { return { id: ref.id, text: text }; });
    },
    remove: function (id) {
      return cloudStore.items().doc(id).delete();
    },
  };

  // ------------------------------------------------------------------

  var backend = useCloud ? cloudStore : localStore;

  window.store = {
    mode: useCloud ? "cloud" : "local",

    // Everything below waits for this. In local mode it resolves immediately.
    ready: backend.start(),

    currentUser: function () { return backend.currentUser(); },
    signIn: function (email, password) { return backend.signIn(email, password); },
    signUp: function (email, password) { return backend.signUp(email, password); },
    signOut: function () { return backend.signOut(); },
    list: function () { return backend.list(); },
    add: function (text) { return backend.add(text); },
    remove: function (id) { return backend.remove(id); },

    askAI: function () {
      return Promise.resolve(${js(c.aiStub)});
    },
  };
})();
`;

const buildAppJs = (c) => `${commentBlock(c.appJsHeader)}

(function () {
  "use strict";

  var el = function (id) { return document.getElementById(id); };

  var user = null;

  function show(node, visible) {
    node.hidden = !visible;
  }

  function setError(message) {
    var box = el("error");
    box.textContent = message || "";
    show(box, !!message);
  }

  function render() {
    show(el("signed-out"), !user);
    show(el("signed-in"), !!user);
    el("who").textContent = user ? user.email : "";
    if (user) refreshList();
  }

  function refreshList() {
    store.list().then(function (items) {
      var list = el("items");
      list.innerHTML = "";
      show(el("empty"), items.length === 0);

      items.forEach(function (item) {
        var li = document.createElement("li");

        var dot = document.createElement("span");
        dot.className = "dot";

        var text = document.createElement("span");
        text.className = "grow";
        text.textContent = item.text;

        var button = document.createElement("button");
        button.type = "button";
        button.className = "link";
        button.textContent = ${js(c.removeBtn)};
        button.addEventListener("click", function () {
          store.remove(item.id).then(refreshList);
        });

        li.appendChild(dot);
        li.appendChild(text);
        li.appendChild(button);
        list.appendChild(li);
      });
    });
  }

  function authenticate(method) {
    setError("");
    var email = el("email").value.trim();
    var password = el("password").value;
    if (!email) return;

    store[method](email, password)
      .then(function (result) {
        user = result;
        el("password").value = "";
        render();
      })
      .catch(function (err) {
        setError(${js(c.errSignIn)} + " (" + (err && err.code ? err.code : err.message) + ")");
      });
  }

  store.ready.then(function () {
    el("mode-text").textContent = store.mode === "cloud" ? ${js(c.modeCloud)} : ${js(c.modeLocal)};
    el("sign-in-hint").textContent = store.mode === "cloud" ? ${js(c.signInHintCloud)} : ${js(c.signInHint)};

    user = store.currentUser();
    render();

    el("sign-in").addEventListener("click", function () { authenticate("signIn"); });
    el("sign-up").addEventListener("click", function () { authenticate("signUp"); });

    el("sign-out").addEventListener("click", function () {
      store.signOut().then(function () {
        user = null;
        render();
      });
    });

    el("add-form").addEventListener("submit", function (event) {
      event.preventDefault();
      setError("");
      var input = el("new-item");
      var text = input.value.trim();
      if (!text) return;
      input.value = "";
      store.add(text).then(refreshList).catch(function () { setError(${js(c.errSave)}); });
    });

    el("ask").addEventListener("click", function () {
      store.askAI(el("question").value).then(function (answer) {
        el("answer").textContent = answer;
        show(el("answer"), true);
      });
    });
  })
  .catch(function (err) {
    // Cloud mode fetches Firebase from the internet before anything can render.
    // Without this, a bad connection or a wrong config leaves the page sitting on
    // "Loading…" forever with the reason only in the console.
    el("mode-text").textContent = ${js(c.errStart)};
    setError(err && err.message ? err.message : String(err));
  });
})();
`;

const buildAppHtml = (brand, c, lang) => {
    const appName = esc(brand.appName);
    const emoji = esc(brand.emoji);
    return `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${appName}</title>
<link rel="stylesheet" href="styles.css" />
</head>
<body>
  <header class="topbar">
    <div class="brand">
      <span class="logo">${emoji}</span>
      <span class="name">${appName}</span>
    </div>
    <a class="cta cta--ghost" href="index.html">${esc(c.backToHome)}</a>
  </header>

  <main class="app-main">
    <div class="app-mode">
      <span class="pip"></span>
      <span id="mode-text">${esc(c.loading)}</span>
    </div>

    <section class="app-card" id="signed-out">
      <h2>${esc(c.signInTitle)}</h2>
      <p class="hint" id="sign-in-hint"></p>
      <div class="app-row">
        <input id="email" type="email" autocomplete="email" placeholder="${esc(c.emailLabel)}" aria-label="${esc(c.emailLabel)}" />
        <input id="password" type="password" autocomplete="current-password" placeholder="${esc(c.passwordLabel)}" aria-label="${esc(c.passwordLabel)}" />
      </div>
      <div class="app-row" style="margin-top: 10px">
        <button type="button" class="cta" id="sign-in">${esc(c.signInBtn)}</button>
        <button type="button" class="cta cta--ghost" id="sign-up">${esc(c.signUpBtn)}</button>
      </div>
      <div class="app-err" id="error" hidden></div>
    </section>

    <div id="signed-in" hidden>
      <div class="app-mode">
        <span class="avatar" id="avatar">&#128100;</span>
        <span id="who"></span>
        <button type="button" class="link" id="sign-out" style="margin-left:auto">${esc(c.signOut)}</button>
      </div>

      <section class="app-card">
        <h2>${esc(c.itemsTitle)}</h2>
        <p class="hint">${esc(c.itemsHint)}</p>
        <form class="app-row" id="add-form">
          <input id="new-item" type="text" placeholder="${esc(c.addPlaceholder)}" aria-label="${esc(c.addPlaceholder)}" />
          <button type="submit" class="cta">${esc(c.addBtn)}</button>
        </form>
        <p class="app-empty" id="empty" hidden>${esc(c.emptyList)}</p>
        <ul class="items" id="items"></ul>
      </section>

      <section class="app-card">
        <h2>${esc(c.aiTitle)}</h2>
        <p class="hint">${esc(c.aiHint)}</p>
        <div class="app-row">
          <input id="question" type="text" placeholder="${esc(c.aiPlaceholder)}" aria-label="${esc(c.aiPlaceholder)}" />
          <button type="button" class="cta" id="ask">${esc(c.aiBtn)}</button>
        </div>
        <div class="ai-reply" id="answer" hidden></div>
      </section>
    </div>
  </main>

  <script src="config.js"></script>
  <script src="store.js"></script>
  <script src="app.js"></script>
</body>
</html>`;
};

const fileList = (locale) =>
    Object.entries(FILE_NOTES[locale])
        .map(([name, note]) => `- \`${name}\` — ${note}`)
        .join("\n");

const buildReadme = (brand, spec, c, locale) => {
    const title = brand.appName || FILE_NOTES[locale]["index.html"];
    return `# ${title}

${lines(c.readmeIntro)}

## ${c.readmeRunTitle}

${lines(c.readmeRunBody)}

## ${c.readmeFilesTitle}

${fileList(locale)}

## ${c.readmeNextTitle}

${lines(c.readmeNextBody)}
`;
};

/**
 * The project brief Claude Code reads.
 *
 * This is where the build spec goes now. It used to be an HTML comment at the top
 * of index.html — the only place a one-file download had — which meant Claude read
 * it only if it happened to open that file, and the student never saw it at all.
 * A markdown file in the project root is read every session, by design.
 */
const buildClaudeMd = (brand, spec, c, locale, specLabels) => {
    const title = brand.appName || "";
    const idea = brand.idea || {};

    // "**Name** — what it does" on one line, "For whom: …" on the next. Every part
    // is optional, so the separators have to be built rather than joined blindly —
    // a missing middle otherwise leaves a dangling em dash on screen.
    const whoItsFor = (spec && spec.whoItsFor) || idea.whoItsFor || "";
    const what = [
        [title ? `**${title}**` : "", (spec && spec.whatItDoes) || idea.whatItDoes || ""]
            .filter(Boolean)
            .join(" — "),
        whoItsFor ? `${specLabels.whoItsFor}: ${whoItsFor}` : "",
    ]
        .filter(Boolean)
        .join("\n\n");

    const bullets = (heading, values) => {
        const items = (Array.isArray(values) ? values : [])
            .map((v) => String(v).trim())
            .filter(Boolean);
        return items.length ? [`### ${heading}`, "", ...items.map((v) => `- ${v}`), ""] : [];
    };

    const specSection = spec
        ? [
              ...bullets(specLabels.features, spec.features),
              ...bullets(specLabels.data, spec.data),
              ...bullets(specLabels.screens, spec.screens),
              ...bullets(specLabels.actions, spec.actions),
              ...bullets(specLabels.notInV1, spec.notInV1),
          ]
        : [lines(c.claudeSpecMissing), ""];

    return `# ${title || "CLAUDE.md"}

${lines(c.claudeIntro)}

## ${c.claudeWhatTitle}

${what || lines(c.claudeSpecMissing)}

## ${c.claudeStackTitle}

${lines(c.claudeStackBody)}

## ${c.claudeFilesTitle}

${fileList(locale)}

## ${c.claudeDataTitle}

${lines(c.claudeDataBody)}

## ${c.claudeAiTitle}

${lines(c.claudeAiBody)}

## ${c.claudeSpecTitle}

${specSection.join("\n").trim()}
`;
};

const buildConnectFirebase = (c) => {
    const steps = c.fbSteps
        .map((step, index) => `### ${index + 1}. ${step.title}\n\n${lines(step.body)}`)
        .join("\n\n");

    return `# Firebase

${lines(c.fbIntro)}

## ${c.fbWarnTitle}

${lines(c.fbWarnBody)}

## ${c.fbStepsTitle}

${steps}

## ${c.fbTroubleTitle}

${lines(c.fbTrouble)}
`;
};

const buildFirestoreRules = (c) => `${commentBlock(c.rulesComment, "// ")}

rules_version = '2';

service cloud.firestore {
  match /databases/{database}/documents {

    match /users/{userId}/items/{itemId} {
      allow read, write: if request.auth != null && request.auth.uid == userId;
    }

  }
}
`;

/** Hosting config for `firebase deploy --only hosting`. The project root IS the site,
 *  so the notes-to-humans and the rules are excluded rather than published.
 *
 *  WHY EVERY INSTRUCTION IN THIS FILE SAYS `--only hosting`. The `firestore` key below
 *  makes a bare `firebase deploy` try to push security rules too, which fails on a
 *  project where Firestore was never turned on — and the course explicitly lets a
 *  student publish without ever creating a database. `--only hosting` skips it, and
 *  costs nothing: CONNECT-FIREBASE.md has them paste the rules into the console rather
 *  than deploy them, so the firestore key here is a convenience for people who go
 *  further, not part of the publish path. */
const buildFirebaseJson = () =>
    JSON.stringify(
        {
            hosting: {
                public: ".",
                ignore: ["firebase.json", "**/.*", "**/node_modules/**", "*.md", "firestore.rules"],
            },
            firestore: {
                rules: "firestore.rules",
            },
        },
        null,
        2
    ) + "\n";

/* ------------------------------------------------------------------------- *
 * Entry point
 * ------------------------------------------------------------------------- */

/**
 * Build the whole project as a `{ path: contents }` map.
 *
 * @param {object}   args
 * @param {object|null} args.project     Their `users_projects` record (brand + idea)
 * @param {object|null} args.spec        The finished build spec, or null
 * @param {object}   args.labels         From buildStarterAppLabels(t) — the landing page
 * @param {object}   args.specLabels     Localized spec headings (buildSpec.file.* )
 * @param {string}   [args.locale="es"]  "es" or "en"
 * @returns {Object<string, string>}
 */
export const buildProjectFiles = ({ project, spec, labels, specLabels, locale = "es" }) => {
    const lang = COPY[locale] ? locale : "es";
    const c = COPY[lang];

    // A student who skipped the Template Studio still gets a working project —
    // every field falls back the same way the landing page's own copy does.
    const brand = {
        appName: project?.appName || "",
        tagline: project?.tagline || "",
        accentColor: project?.accentColor || DEFAULT_BRAND.accentColor,
        emoji: project?.emoji || DEFAULT_BRAND.emoji,
        idea: project?.idea || {},
    };

    return {
        "index.html": buildStarterAppHtml(brand, labels, { inline: false, lang }),
        "app.html": buildAppHtml(brand, c, lang),
        "styles.css": `${starterAppCss(esc(brand.accentColor))}\n`,
        "config.js": buildConfigJs(brand, c),
        "store.js": buildStoreJs(c),
        "app.js": buildAppJs(c),
        "CLAUDE.md": buildClaudeMd(brand, spec, c, lang, specLabels),
        "README.md": buildReadme(brand, spec, c, lang),
        "CONNECT-FIREBASE.md": buildConnectFirebase(c),
        "firebase.json": buildFirebaseJson(),
        "firestore.rules": buildFirestoreRules(c),
    };
};

/** Exported for the test that asserts every generated file has a description. */
export const PROJECT_FILE_NAMES = Object.keys(FILE_NOTES.es);

export default buildProjectFiles;
